import React from 'react';

interface InvoiceLine {
  invoice_id: string;
  invoice_name: string;
  invoice_number: string;
  billing_start: string;
  billing_end: string;
  amount: number;
  invoice_status: string;
  is_latest: boolean;
  used_for_billing_calculation: boolean;
  should_be_considered_for_billing: boolean;
  exclusion_reasons: string[];
}

interface FinalizedInvoiceLine {
  invoice_id: string;
  invoice_name: string;
  invoice_number: string;
  billing_start: string;
  billing_end: string;
  amount: number;
}

interface ContractInvoice {
  invoice_id: string;
  invoice_name: string;
  invoice_number: string | number;
  billing_period_start: string;
  billing_period_end: string;
  invoice_status: string;
  amount: number;
  has_finalized_lines: boolean;
  isdeleted: boolean;
}

interface ValidationInfo {
  max_invoice_billing_period_end?: string | null;
  finalized_invoice_count: number;
  deleted_invoice_count: number;
  last_contract_invoice: {
    invoice_id?: string | null;
    invoice_number?: string | number | null;
    billing_period_end?: string | null;
  };
}

interface ContractLevelCalculation {
  next_billing_period: [string, string] | null;
  explanation: string;
  validation_note?: string;
}

interface DebugInfo {
  contract_id: string;
  contract_name: string;
  contract_number: string | number;
  contract_lines: ContractLineDebug[];
  contract_level_calculation: ContractLevelCalculation;
  all_invoices: ContractInvoice[];
  validation_info: ValidationInfo;
}

interface ContractLineDebug {
  contractline_id: string;
  contractline_name: string;
  current_last_billing_start: string | null;
  current_last_billing_end: string | null;
  current_upcoming_billing_start: string | null;
  current_upcoming_billing_end: string | null;
  found_invoice_lines: InvoiceLine[];
  finalized_invoice_lines: FinalizedInvoiceLine[];
  calculated_last_billing: [string, string] | null;
  calculated_next_period: [string, string] | null;
  calculation_explanation: string;
  billing_calculation_note?: string;
  calculation_warning?: string;
}

interface DebugBillingPeriodsModalProps {
  debugInfo: DebugInfo;
  onClose: () => void;
}

// Simple date formatter for display
const formatDate = (dateString: string): string => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString();
  } catch {
    return dateString;
  }
};

// Simple currency formatter
const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'EUR'
  }).format(amount);
};

export const DebugBillingPeriodsModal: React.FC<DebugBillingPeriodsModalProps> = ({ debugInfo, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[95vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white z-10">
          <h2 className="text-xl font-bold text-gray-800">
            Billing Periods Debug Information
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            title="Close"
            aria-label="Close"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        {/* Content */}
        <div className="p-6 pb-20">
          {/* Contract Info */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-700 mb-3">Contract Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p><strong>Contract:</strong> {debugInfo.contract_name} ({debugInfo.contract_number})</p>
                <p><strong>ID:</strong> {debugInfo.contract_id}</p>
              </div>
            </div>
          </div>
          
          {/* All Invoices (Contract-Level View) */}
          {debugInfo.all_invoices.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-700 mb-3">
                All Contract Invoices ({debugInfo.all_invoices.length})
              </h3>
              <p className="text-sm text-gray-600 mb-3">
                This shows all invoices for the entire contract, regardless of which contract line they belong to.
              </p>
              <div className="overflow-x-auto border border-gray-200 rounded">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-gray-700">Invoice</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-700">Billing Period</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-700">Status</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-700">Amount</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-700">Finalized Lines</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-700">Deleted</th>
                    </tr>
                  </thead>
                  <tbody>
                    {debugInfo.all_invoices.map((inv) => (
                      <tr key={inv.invoice_id} className="border-t hover:bg-gray-50">
                        <td className="px-3 py-2">
                          <div className="font-medium">{inv.invoice_number}</div>
                          <div className="text-xs text-gray-500">{inv.invoice_name}</div>
                        </td>
                        <td className="px-3 py-2">
                          {formatDate(inv.billing_period_start)} - {formatDate(inv.billing_period_end)}
                        </td>
                        <td className="px-3 py-2">
                          <span className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${
                            inv.invoice_status === 'finalized' ? 'bg-green-100 text-green-800' : 
                              inv.invoice_status === 'draft' ? 'bg-yellow-100 text-yellow-800' : 
                              inv.invoice_status === 'cancelled' ? 'bg-red-100 text-red-800' : 
                              'bg-gray-100 text-gray-800'}
                          `}>
                            {inv.invoice_status}
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          {formatCurrency(inv.amount)}
                        </td>
                        <td className="px-3 py-2 text-center">
                          {inv.has_finalized_lines ? (
                            <span className="text-green-600" title="Has finalized invoice lines">✓</span>
                          ) : (
                            <span className="text-gray-400" title="No finalized invoice lines">✗</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-center">
                          {inv.isdeleted ? (
                            <span className="text-red-600" title="Invoice is deleted">✗</span>
                          ) : (
                            <span className="text-green-600" title="Invoice is active">✓</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          
          {/* Validation Info */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-700 mb-3">Validation Information</h3>
            <div className="p-4 bg-blue-50 rounded-lg space-y-2">
              <p className="text-sm">
                <strong>Finalized Invoices Count:</strong> {debugInfo.validation_info.finalized_invoice_count}
              </p>
              {debugInfo.validation_info.deleted_invoice_count > 0 && (
                <p className="text-sm text-orange-600">
                  <strong>Deleted Invoices Count:</strong> {debugInfo.validation_info.deleted_invoice_count} 
                  <span className="text-xs">(excluded from calculations)</span>
                </p>
              )}
              {debugInfo.validation_info.max_invoice_billing_period_end && (
                <p className="text-sm">
                  <strong>Max Invoice Billing Period End:</strong> {formatDate(debugInfo.validation_info.max_invoice_billing_period_end)}
                </p>
              )}
              {debugInfo.validation_info.last_contract_invoice && debugInfo.validation_info.last_contract_invoice.invoice_id && (
                <div className="text-sm">
                  <p><strong>Last Contract Invoice:</strong> #{debugInfo.validation_info.last_contract_invoice.invoice_number}</p>
                  {debugInfo.validation_info.last_contract_invoice.billing_period_end && (
                    <p className="ml-4">
                      <strong>Billing Period End:</strong> {formatDate(debugInfo.validation_info.last_contract_invoice.billing_period_end)}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
          
          {/* Contract Lines */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-700 mb-3">Contract Lines</h3>
            {debugInfo.contract_lines.length === 0 ? (
              <p className="text-gray-500">No contract lines found.</p>
            ) : (
              <div className="space-y-4 pr-2">
                {debugInfo.contract_lines.map((line, index) => (
                  <div key={line.contractline_id} className="mb-4 p-4 border border-gray-200 rounded-lg shadow-sm">
                  <div className="flex justify-between items-start mb-3">
                    <h4 className="font-medium text-gray-800">
                      {index + 1}. {line.contractline_name}
                    </h4>
                    <span className="text-sm text-gray-500">ID: {line.contractline_id}</span>
                  </div>
                  
                  {/* Current vs Calculated Comparison */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div className="bg-gray-50 p-3 rounded">
                      <h5 className="font-semibold text-gray-700 mb-2 text-sm uppercase tracking-wide">
                        Current Database Values
                      </h5>
                      <div className="space-y-1 text-sm">
                        <p>
                          <strong>Last Billing:</strong> 
                          {line.current_last_billing_start && line.current_last_billing_end ?
                            `${formatDate(line.current_last_billing_start)} - ${formatDate(line.current_last_billing_end)}` :
                            <span className="text-gray-400">None</span>}
                        </p>
                        <p>
                          <strong>Upcoming Billing:</strong> 
                          {line.current_upcoming_billing_start && line.current_upcoming_billing_end ?
                            `${formatDate(line.current_upcoming_billing_start)} - ${formatDate(line.current_upcoming_billing_end)}` :
                            <span className="text-gray-400">None</span>}
                        </p>
                      </div>
                    </div>
                    
                    <div className="bg-blue-50 p-3 rounded">
                      <h5 className="font-semibold text-blue-700 mb-2 text-sm uppercase tracking-wide">
                        Calculated Values
                      </h5>
                      <div className="space-y-1 text-sm">
                        <p>
                          <strong>Last Billing:</strong> 
                          {line.calculated_last_billing ?
                            `${formatDate(line.calculated_last_billing[0])} - ${formatDate(line.calculated_last_billing[1])}` :
                            <span className="text-gray-400">None</span>}
                        </p>
                        <p>
                          <strong>Next Period:</strong> 
                          {line.calculated_next_period ?
                            `${formatDate(line.calculated_next_period[0])} - ${formatDate(line.calculated_next_period[1])}` :
                            <span className="text-gray-400">None</span>}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Calculation Explanation */}
                  <div className="mb-4 p-3 bg-gray-50 rounded">
                    <strong className="text-sm text-gray-700">How this is calculated:</strong>
                    <p className="text-sm text-gray-600 mt-1">
                      {line.calculation_explanation}
                    </p>
                  </div>
                  
                  {/* Billing Calculation Note */}
                  {line.billing_calculation_note && (
                    <div className="mt-4 p-3 bg-blue-50 rounded border border-blue-100">
                      <p className="text-sm text-blue-700 flex items-start">
                        <span className="mr-2 mt-0.5">📊</span>
                        <strong>Billing Calculation Note:</strong> {line.billing_calculation_note}
                      </p>
                    </div>
                  )}
                  
                  {/* Calculation Warning */}
                  {line.calculation_warning && (
                    <div className="mt-4 p-3 bg-yellow-50 rounded border border-yellow-200">
                      <p className="text-sm text-yellow-700 flex items-start">
                        <span className="mr-2 mt-0.5">⚠️</span>
                        <strong>Warning:</strong> {line.calculation_warning}
                      </p>
                    </div>
                  )}
                  
                  {/* Finalized Invoice Lines (used for billing calculations) */}
                  {line.finalized_invoice_lines && line.finalized_invoice_lines.length > 0 ? (
                    <div className="mt-4">
                      <h5 className="font-semibold text-green-700 mb-2 flex items-center">
                        <span className="mr-2">✅</span>
                        {line.finalized_invoice_lines.length} Finalized Invoice Line(s) Used for Billing:
                      </h5>
                      <div className="overflow-x-auto border border-green-200 rounded bg-green-50">
                        <table className="min-w-full text-sm">
                          <thead className="bg-green-100">
                            <tr>
                              <th className="px-3 py-2 text-left font-medium text-gray-700">Invoice</th>
                              <th className="px-3 py-2 text-left font-medium text-gray-700">Period</th>
                              <th className="px-3 py-2 text-left font-medium text-gray-700">Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            {line.finalized_invoice_lines.map((inv) => (
                              <tr key={inv.invoice_id} className="border-t hover:bg-green-100">
                                <td className="px-3 py-2">
                                  <div className="font-medium">{inv.invoice_number}</div>
                                  <div className="text-xs text-gray-600">{inv.invoice_name}</div>
                                </td>
                                <td className="px-3 py-2">
                                  {formatDate(inv.billing_start)} - {formatDate(inv.billing_end)}
                                </td>
                                <td className="px-3 py-2">
                                  {formatCurrency(inv.amount)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : null}

                  {/* Found Invoice Lines */}
                  {line.found_invoice_lines.length > 0 ? (
                    <div className="mt-4">
                      <h5 className="font-semibold text-gray-700 mb-2 flex items-center">
                        <span className="mr-2">📋</span>
                        All {line.found_invoice_lines.length} Invoice Line(s):
                      </h5>
                      <div className="overflow-x-auto border border-gray-200 rounded">
                        <table className="min-w-full text-sm">
                          <thead className="bg-gray-100">
                            <tr>
                              <th className="px-3 py-2 text-left font-medium text-gray-700">Invoice</th>
                              <th className="px-3 py-2 text-left font-medium text-gray-700">Period</th>
                              <th className="px-3 py-2 text-left font-medium text-gray-700">Amount</th>
                              <th className="px-3 py-2 text-left font-medium text-gray-700">Status</th>
                              <th className="px-3 py-2 text-left font-medium text-gray-700">Used for Calc</th>
                            </tr>
                          </thead>
                          <tbody>
                            {line.found_invoice_lines.map((inv) => (
                              <tr key={inv.invoice_id} className="border-t hover:bg-gray-50">
                                <td className="px-3 py-2">
                                  <div className="font-medium">{inv.invoice_number}</div>
                                  <div className="text-xs text-gray-500">{inv.invoice_name}</div>
                                  {inv.is_latest && (
                                    <span className="inline-block px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full mt-1">
                                      Latest
                                    </span>
                                  )}
                                </td>
                                <td className="px-3 py-2">
                                  {formatDate(inv.billing_start)} - {formatDate(inv.billing_end)}
                                </td>
                                <td className="px-3 py-2">
                                  {formatCurrency(inv.amount)}
                                </td>
                                <td className="px-3 py-2">
                                  <span className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${
                                    inv.invoice_status === 'finalized' ? 'bg-green-100 text-green-800' : 
                                      inv.invoice_status === 'draft' ? 'bg-yellow-100 text-yellow-800' : 
                                      inv.invoice_status === 'cancelled' ? 'bg-red-100 text-red-800' : 
                                      'bg-gray-100 text-gray-800'}
                                  `}>
                                    {inv.invoice_status}
                                  </span>
                                </td>
                                <td className="px-3 py-2 text-center">
                                  {inv.used_for_billing_calculation ? (
                                    <span className="text-green-600" title="Used for billing period calculation">✓</span>
                                  ) : (
                                    <span className="text-gray-400" title="Not used for billing period calculation">✗</span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : (
                    <div className="mt-4 p-3 bg-yellow-50 rounded">
                      <p className="text-sm text-yellow-700 flex items-center">
                        <span className="mr-2">⚠️</span>
                        No finalized invoice lines found for this contract line.
                      </p>
                    </div>
                  )}
                </div>
              ))}
              </div>
            )}
          </div>
          
          {/* Contract-Level Calculation */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-700 mb-3">Contract-Level Calculation</h3>
            <div className="p-4 bg-blue-50 rounded-lg">
              <p className="mb-2">
                <strong>Next Billing Period:</strong> 
                {debugInfo.contract_level_calculation.next_billing_period ?
                  `${formatDate(debugInfo.contract_level_calculation.next_billing_period[0])} - 
                   ${formatDate(debugInfo.contract_level_calculation.next_billing_period[1])}` :
                  <span className="text-gray-400">N/A</span>}
              </p>
              <p className="text-sm text-gray-600 mb-2">
                {debugInfo.contract_level_calculation.explanation}
              </p>
              {debugInfo.contract_level_calculation.validation_note && (
                <div className="mt-3 p-3 bg-yellow-50 rounded border border-yellow-200">
                  <p className="text-sm text-yellow-700 flex items-start">
                    <span className="mr-2 mt-0.5">⚠️</span>
                    <strong>Validation Note:</strong> {debugInfo.contract_level_calculation.validation_note}
                  </p>
                </div>
              )}
            </div>
          </div>
          
          {/* Summary */}
          <div className="p-4 bg-gray-50 rounded-lg">
            <h3 className="text-lg font-semibold text-gray-700 mb-3">Summary</h3>
            <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
              <li>Billing periods are calculated based on <strong>finalized invoices only</strong></li>
              <li>Last billing period comes from the most recent finalized invoice line <strong>per contract line</strong></li>
              <li>Contract-level next billing period uses the <strong>maximum billing_period_end</strong> from all FINALIZED invoices for the contract</li>
              <li>Next billing period starts the day after the last billing period ends</li>
              <li>This calculation is <strong>consistent with how new invoices are generated</strong></li>
              <li>Draft, voided, and deleted invoices are <strong>ignored</strong> in calculations</li>
              <li>Invoice-level (<code>billing_period_end</code>) dates are used for contract-level calculations</li>
            </ul>
          </div>
        </div>
        
        {/* Footer */}
        <div className="p-4 border-t border-gray-200 text-right bg-gray-50 rounded-b-lg">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default DebugBillingPeriodsModal;
